import { projectDriveLabels } from '../../shared/drives';
import { calendarDaysBetween, melbourneDate } from '../../shared/dates';
import type { ProjectPhase } from '../../shared/types';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function projectListDate(iso: string, weekday = false): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return `${weekday ? `${weekdays[d.getUTCDay()]} ` : ''}${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}
export interface ProjectListMetaSource {
  phase: ProjectPhase; client_name: string; shoot_date: string | null;
  due_date: string | null; updated_at: number;
  next_task_title: string | null; next_task_due: string | null; open_tasks: number;
}
export function projectListMeta(p: ProjectListMetaSource, today: string) {
  let dateText = '—';
  let hot = false;
  if (p.phase === 'on_hold') dateText = `Paused ${projectListDate(melbourneDate(p.updated_at))}`;
  else if ((p.phase === 'shoot' || p.phase === 'pre-production') && p.shoot_date) {
    const days = calendarDaysBetween(today, p.shoot_date);
    hot = days >= 0 && days <= 7;
    dateText = `Shoot ${projectListDate(p.shoot_date, p.phase === 'shoot')}`;
  } else if (p.phase === 'lead' && !p.due_date) dateText = 'Kickoff TBC';
  else if (p.due_date) dateText = `Due ${projectListDate(p.due_date)}`;
  const overdue = !!p.next_task_title && !!p.next_task_due && p.next_task_due < today;
  return {
    dateText, hot, text: [p.client_name, dateText].filter(Boolean).join(' · '), overdue,
    nextTask: p.next_task_title ? `${p.next_task_title}${overdue ? ' — overdue' : ''}` : null,
    open: `${p.open_tasks} open`,
  };
}
export function projectListVitals(nextShoot: string | undefined, openTasks: number, deliveredFY: number) {
  return {
    next: `NEXT SHOOT · ${nextShoot ? projectListDate(nextShoot, true).toUpperCase() : '—'}`,
    counts: `${openTasks} OPEN · ${deliveredFY} DELIVERED`,
  };
}
export interface ProjectListFilters {
  view: 'active' | 'delivered' | 'all' | 'clean_up'; query: string; phase: string | null;
  client: string | null; video_type: string | null; archive: string | null;
  sort: string; showVoided: boolean;
}
export const projectListDefaults = (): ProjectListFilters => ({
  view: 'active', query: '', phase: null, client: null, video_type: null, archive: null,
  sort: 'next:asc', showVoided: false,
});
export function projectListMatches(p: {
  name: string; client_name: string; phase: ProjectPhase; voided_at: number | null;
  drive_labels?: string[]; video_type: string | null; archive_drive_1: string | null; archive_drive_2: string | null;
}, f: ProjectListFilters): boolean {
  const q = f.query.trim().toLowerCase();
  return (f.showVoided || p.voided_at === null)
    && (!q || [p.name, p.client_name].some(s => s.toLowerCase().includes(q)))
    && (!f.client || p.client_name === f.client)
    && (!f.video_type || p.video_type === f.video_type)
    && (!f.archive || projectDriveLabels(p).includes(f.archive))
    && (!f.phase || p.phase === f.phase)
    && (f.view === 'all' || (f.view === 'delivered' || f.view === 'clean_up' ? p.phase === f.view : !['delivered', 'clean_up', 'closed'].includes(p.phase)));
}
function defaultListSort(view: ProjectListFilters['view']): string {
  return view === 'delivered' ? 'delivered:desc' : view === 'clean_up' ? 'delivered:asc' : view === 'all' ? 'number:desc' : 'next:asc';
}
export function projectListFromSaved(p: Record<string, string>): ProjectListFilters {
  const view = p.view === 'all' || p.view === 'delivered' || p.view === 'clean_up' ? p.view : 'active';
  return { view, query: p.query ?? '', phase: p.phase || null, client: p.client || null,
    video_type: p.video_type || null, archive: p.archive || null, showVoided: false,
    sort: p.sort || defaultListSort(view) };
}
export function projectListSaved(f: ProjectListFilters) {
  const defaultSort = defaultListSort(f.view);
  return { view: f.view, query: f.query, phase: f.phase, client: f.client, video_type: f.video_type,
    archive: f.archive, sort: f.sort === defaultSort ? null : f.sort };
}
